import { Button, Icon, Typography } from '@/components';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockedSlots } from '@/features/offers/calendar/hooks';

// Types:
export type ProposedSlot = {
  start_time: string;
  end_time: string;
};

type SelectedSlot = {
  key: string;
  date: string;
  time: string;
  start_time: string;
  end_time: string;
  weekNumber: number;
};

type SellerAcceptEoiFormProps = {
  readonly onBack: () => void;
  readonly onSubmit: (slots: ProposedSlot[]) => void;
  readonly isLoading: boolean;
  readonly opportunityId?: string;
};

// Consts:
const HOURS = ['10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
const DAYS_OF_WEEK = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MIN_SLOTS = 4;
const MIN_WEEKS = 2;

// Helpers:
function getISOWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

export function SellerAcceptEoiForm({ onBack, onSubmit, isLoading, opportunityId }: SellerAcceptEoiFormProps) {
  const { t } = useTranslation(['offers']);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<SelectedSlot[]>([]);

  // Validación: mínimo 4 slots en al menos 2 semanas
  const uniqueWeeks = useMemo(() => new Set(selectedSlots.map((s) => s.weekNumber)).size, [selectedSlots]);
  const hasEnoughSlots = selectedSlots.length >= MIN_SLOTS;
  const hasEnoughWeeks = uniqueWeeks >= MIN_WEEKS;
  const canSubmit = hasEnoughSlots && hasEnoughWeeks && !isLoading;

  // Generar los 21 días (3 semanas)
  const calendarDays = useMemo(() => {
    const today = new Date();
    const start = new Date(today);
    const dayOfWeek = start.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    start.setDate(start.getDate() - daysToSubtract);

    return Array.from({ length: 21 }, (_, i) => {
      const date = new Date(start);
      date.setDate(date.getDate() + i);
      return date;
    });
  }, []);

  // Blocked slots
  const dateRange = useMemo(() => {
    if (calendarDays.length === 0) return { from: '', to: '' };
    const first = calendarDays[0].toISOString().split('T')[0];
    const last = calendarDays[calendarDays.length - 1].toISOString().split('T')[0];
    return { from: first, to: last };
  }, [calendarDays]);

  const { isBlockedDay, isBlockedTime } = useBlockedSlots({
    dateFrom: dateRange.from,
    dateTo: dateRange.to,
    opportunityId,
  });

  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }
    return result;
  }, [calendarDays]);

  const calendarTitle = useMemo(() => {
    if (calendarDays.length === 0) return '';
    const first = calendarDays[0];
    const last = calendarDays[calendarDays.length - 1];
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

    const firstMonth = formatter.format(first);
    const lastMonth = formatter.format(last);

    return firstMonth === lastMonth ? firstMonth : `${firstMonth} - ${lastMonth}`;
  }, [calendarDays]);

  const isPastDate = useCallback((date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  }, []);

  const MINIMUM_HOURS_AHEAD = 2;
  const isPastTime = useCallback(
    (hour: string) => {
      if (!selectedDate) return false;
      const now = new Date();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(selectedDate);
      selected.setHours(0, 0, 0, 0);

      // Si no es hoy, no está en el pasado
      if (selected.getTime() !== today.getTime()) return false;

      // Si es hoy, comparar la hora
      const [hourPart] = hour.split(':');
      return Number.parseInt(hourPart) <= now.getHours() + MINIMUM_HOURS_AHEAD;
    },
    [selectedDate]
  );

  const isWeekend = useCallback((date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  }, []);

  const handleTimeSelect = useCallback(
    (hour: string) => {
      if (!selectedDate) return;

      const dateStr = selectedDate.toISOString().split('T')[0];
      const [hourPart] = hour.split(':');
      const endHour = String(Number.parseInt(hourPart) + 1).padStart(2, '0');

      const slotKey = `${dateStr}-${hour}`;
      const exists = selectedSlots.some((s) => s.key === slotKey);

      if (exists) {
        setSelectedSlots((prev) => prev.filter((s) => s.key !== slotKey));
      } else {
        const dayName = selectedDate.toLocaleString('en-US', { weekday: 'short' });
        const dayNum = selectedDate.getDate();
        const month = selectedDate.toLocaleString('en-US', { month: 'short' });

        setSelectedSlots((prev) => [
          ...prev,
          {
            key: slotKey,
            date: `${dayName} ${dayNum} ${month}`,
            time: `${hour}-${endHour}:00`,
            start_time: `${dateStr}T${hour}:00`,
            end_time: `${dateStr}T${endHour}:00:00`,
            weekNumber: getISOWeekNumber(selectedDate),
          },
        ]);
      }
    },
    [selectedDate, selectedSlots]
  );

  const removeSlot = useCallback((slotKey: string) => {
    setSelectedSlots((prev) => prev.filter((s) => s.key !== slotKey));
  }, []);

  const formatSelectedDate = useCallback((date: Date) => {
    return date.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }, []);

  const handleSubmit = useCallback(() => {
    const slots = selectedSlots.map(({ start_time, end_time }) => ({ start_time, end_time }));
    onSubmit(slots);
  }, [selectedSlots, onSubmit]);

  return (
    <Card className="border-primary-200 mb-6 border bg-emerald-50/50 backdrop-blur-sm">
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center">{t('Share availability')}</CardTitle>
        <button
          onClick={onBack}
          disabled={isLoading}
          className="flex cursor-pointer items-center gap-1 text-slate-600 transition-colors hover:text-slate-900 disabled:opacity-50"
        >
          <Icon name="chevron-left" className="h-4 w-4" />
          <span className="text-sm font-medium">{t('Back')}</span>
        </button>
      </CardHeader>

      <CardContent>
        {/* Info */}
        <div className="mb-6">
          <Typography className="text-sm text-slate-700">
            {t(
              'Please, propose a minimum of 4 slots across at least 2 different weeks to schedule the Kick-Off Meeting.'
            )}
          </Typography>
        </div>

        {/* Calendar */}
        <div className="border-primary-200 mb-6 rounded-xl border bg-white p-4">
          <h3 className="mb-4 font-bold text-slate-900">{calendarTitle}</h3>

          {weeks.map((week, weekIdx) => (
            <div key={weekIdx} className="mb-2">
              {weekIdx === 0 && (
                <div className="mb-2 grid grid-cols-7 gap-2">
                  {DAYS_OF_WEEK.map((day, i) => (
                    <div key={`${day}-${i}`} className="py-1 text-center text-xs font-semibold text-slate-600">
                      {day}
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-7 gap-2">
                {week.map((date, dayIdx) => {
                  const isSelected = selectedDate?.toDateString() === date.toDateString();
                  const isPast = isPastDate(date);
                  const weekend = isWeekend(date);
                  // const disabled = isPast || weekend || isLoading;
                  const isBlocked = isBlockedDay(date);
                  const disabled = isPast || weekend || isBlocked || isLoading;
                  const dateStr = date.toISOString().split('T')[0];
                  const hasSlots = selectedSlots.some((s) => s.key.startsWith(dateStr));

                  return (
                    <button
                      key={dayIdx}
                      onClick={() => !disabled && setSelectedDate(date)}
                      disabled={disabled}
                      className={`relative aspect-square rounded-lg text-sm font-semibold transition-all ${
                        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                      } ${
                        isSelected
                          ? 'border-2 border-emerald-700 bg-emerald-700 text-white'
                          : hasSlots
                            ? 'border-2 border-emerald-300 bg-emerald-100 text-emerald-700'
                            : disabled
                              ? 'bg-slate-100 text-slate-400'
                              : 'border-2 border-slate-200 bg-white text-slate-900 hover:border-emerald-300'
                      }`}
                    >
                      {date.getDate()}
                      {hasSlots && !isSelected && (
                        <div className="absolute bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-emerald-600" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <hr />

        {/* Time slots for selected date */}
        {selectedDate && (
          <div className="mt-4">
            <div className="mb-2 flex justify-between">
              <Typography as="p" size="body-2">
                {t('Times for')} <span className="font-semibold">{formatSelectedDate(selectedDate)}</span>
              </Typography>
              <p className="text-xs text-slate-500">UTC+2</p>
            </div>

            <div className="mb-6 grid grid-cols-3 gap-2">
              {HOURS.map((hour) => {
                const dateStr = selectedDate.toISOString().split('T')[0];
                const slotKey = `${dateStr}-${hour}`;
                const isSelected = selectedSlots.some((s) => s.key === slotKey);
                const pastTime = isPastTime(hour);
                // const disabled = isLoading || pastTime;
                const blockedTime = isBlockedTime(selectedDate, hour);
                const disabled = isLoading || pastTime || blockedTime;

                return (
                  <button
                    key={hour}
                    onClick={() => handleTimeSelect(hour)}
                    disabled={disabled}
                    className={`rounded-lg border-2 px-3 py-2 text-sm font-medium transition-all ${
                      disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                    } ${
                      isSelected
                        ? 'border-emerald-700 bg-emerald-700 text-white'
                        : disabled
                          ? 'border-slate-200 bg-slate-100 text-slate-400'
                          : 'border-slate-200 bg-white text-slate-900 hover:border-emerald-300'
                    }`}
                  >
                    {hour}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <hr />

        {/* Selected slots summary */}
        <div className="mt-4 mb-6">
          <div className="mb-3 flex items-center justify-between">
            <Typography as="p" size="body-2" className="font-semibold uppercase">
              {t('Selected Slots')}
            </Typography>
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-700 text-xs font-semibold text-white">
              {selectedSlots.length}
            </span>
          </div>

          {/* Validation hints */}
          <div className="mb-3 flex flex-wrap gap-3 text-xs">
            <span className={`flex items-center gap-1 ${hasEnoughSlots ? 'text-primary' : 'text-red-600'}`}>
              <Icon name={hasEnoughSlots ? 'check' : 'info'} className="h-3 w-3" />
              {t('Min. {{count}} slots', { count: MIN_SLOTS })}
            </span>
            <span className={`flex items-center gap-1 ${hasEnoughWeeks ? 'text-primary' : 'text-red-600'}`}>
              <Icon name={hasEnoughWeeks ? 'check' : 'info'} className="h-3 w-3" />
              {t('Min. {{count}} different weeks', { count: MIN_WEEKS })}
            </span>
          </div>

          <div className="space-y-2">
            {selectedSlots.length === 0 ? (
              <p className="text-sm text-slate-500 italic">{t('No slots selected yet')}</p>
            ) : (
              selectedSlots.map((slot) => (
                <div
                  key={slot.key}
                  className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-3"
                >
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">{slot.date}</p>
                    <span className="text-slate-300">·</span>
                    <p className="text-sm text-slate-600">{slot.time}</p>
                  </div>
                  <button
                    onClick={() => removeSlot(slot.key)}
                    disabled={isLoading}
                    className="ml-2 rounded p-1 transition-colors hover:bg-red-100 disabled:opacity-50"
                  >
                    <Icon name="x" className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Submit */}
        <Button
          size="sm"
          color="primary"
          className="w-full"
          onClick={handleSubmit}
          disabled={!canSubmit}
          icon={'arrow-right'}
        >
          {isLoading ? t('Sending...') : t('Share availability with buyer')}
        </Button>
      </CardContent>
    </Card>
  );
}
